import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    Button,
    Chip,
    Avatar,
    LinearProgress,
    Stack,
    IconButton,
    Tooltip
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import { api } from 'common';
import { FONT_FAMILY } from 'common/sharedFunctions';
import { Link } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCarOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SensorsIcon from '@mui/icons-material/Sensors';

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers } = api;

    function formatAmount(value, decimal, country) {
        const number = parseFloat(value || 0);
        if (country === "Vietnam") {
            return number.toLocaleString("vi-VN", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        } else {
            return number.toLocaleString("en-US", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        }
    }

    const formatCurrencyDisplay = (amount) => {
        const sym = settings?.symbol || '₦';
        const formatted = formatAmount(amount, settings?.decimal || 2, settings?.country);
        return settings?.swipe_symbol ? `${formatted} ${sym}` : `${sym} ${formatted}`;
    };

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, []);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED' || bkg.status === 'ACCEPTED' || bkg.status === 'REACHED');
            const drivers = usersdata.drivers;
            let locs = [];

            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = '/bike.png';
                    let bookingRef = null;
                    if (cars) {
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j].name === drivers[i].carType) {
                                carImage = cars[j].image;
                            }
                        }
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === drivers[i].uid) {
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: (drivers[i].firstName || '') + ' ' + (drivers[i].lastName || ''),
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage,
                        bookingRef: bookingRef,
                        fleetadmin: drivers[i].fleetadmin
                    });
                }
            }
            if (auth.profile.usertype === 'fleetadmin') {
                locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
            }
            setLocations(locs);
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin')) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount || 0);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount || 0);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount || 0);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal || 2));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal || 2));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal || 2));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    const totalCompletedAnnual = allCompleteCount.reduce((a, b) => a + b, 0);
    const totalCancelledAnnual = allCancelCount.reduce((a, b) => a + b, 0);
    const totalBookingsAll = (bookinglistdata.bookings || []).length;
    const completionRate = (totalCompletedAnnual + totalCancelledAnnual) > 0
        ? Math.round((totalCompletedAnnual / (totalCompletedAnnual + totalCancelledAnnual)) * 100)
        : 100;

    const chartOptions = {
        chart: {
            type: 'area',
            height: 310,
            toolbar: { show: false },
            fontFamily: FONT_FAMILY,
            zoom: { enabled: false },
            sparkline: { enabled: false }
        },
        colors: ['#059669', '#E11D48'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.25,
                opacityTo: 0.02,
                stops: [0, 90, 100]
            }
        },
        xaxis: {
            categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
            labels: {
                style: { fontFamily: FONT_FAMILY, colors: '#94A3B8', fontSize: '11px', fontWeight: 600 }
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            labels: {
                style: { fontFamily: FONT_FAMILY, colors: '#94A3B8', fontSize: '11px', fontWeight: 600 }
            }
        },
        grid: {
            borderColor: '#F1F5F9',
            strokeDashArray: 3,
            padding: { top: 0, right: 10, bottom: 0, left: 10 }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            fontSize: '12px',
            markers: { width: 8, height: 8, radius: 8 }
        },
        tooltip: {
            theme: 'light',
            style: { fontFamily: FONT_FAMILY, fontSize: '12px' }
        }
    };

    const chartSeries = [
        { name: t('completed_bookings', { defaultValue: 'Fulfilled Deliveries' }), data: allCompleteCount },
        { name: t('cancelled_bookings', { defaultValue: 'Cancelled' }), data: allCancelCount }
    ];

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 1.5, sm: 2.5, md: 3 } }}>
                {/* ── Executive Top Header ── */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 1.5 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em' }}>
                            {t('dashboard_text', { defaultValue: 'Operations Dashboard' })}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.84rem', mt: 0.3 }}>
                            Real-time platform metrics, dispatch telemetry & settlement performance.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderRadius: '8px', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981', boxShadow: '0 0 6px #10B981' }} />
                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: '#334155', fontSize: '0.75rem' }}>
                                Telemetry Active
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => dispatch(fetchDrivers('web'))}
                            startIcon={<RefreshIcon fontSize="small" />}
                            sx={{
                                fontFamily: FONT_FAMILY,
                                textTransform: 'none',
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderRadius: '8px',
                                backgroundColor: '#FFFFFF',
                                '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' }
                            }}
                        >
                            Sync
                        </Button>
                    </Stack>
                </Box>

                {/* ── Executive 4-Column Primary Metrics ── */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DashboardCard
                            title="Today's Revenue"
                            value={formatCurrencyDisplay(dailygross)}
                            subtitle="Net collected today"
                            badge="Daily Gross"
                            badgeType="success"
                            icon={<AttachMoneyIcon sx={{ fontSize: 18 }} />}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <DashboardCard
                            title="Monthly Revenue"
                            value={formatCurrencyDisplay(monthlygross)}
                            subtitle="Current billing cycle"
                            badge="Month-to-Date"
                            badgeType="info"
                            icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <DashboardCard
                            title="All-Time Gross (GMV)"
                            value={formatCurrencyDisplay(totalgross)}
                            subtitle="Platform commission total"
                            badge="Cumulative"
                            badgeType="neutral"
                            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <DashboardCard
                            title="Total Bookings"
                            value={totalBookingsAll.toLocaleString()}
                            subtitle={`${completionRate}% success rate`}
                            badge={`${totalCompletedAnnual} Fulfilled`}
                            badgeType="success"
                            icon={<LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />}
                        />
                    </Grid>
                </Grid>

                {/* ── Middle Row: Live Fleet Radar & Operations Snapshot ── */}
                <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                    {/* Live Fleet Radar */}
                    <Grid item xs={12} lg={8}>
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '14px',
                                border: '1px solid #E2E8F0',
                                overflow: 'hidden',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981', boxShadow: '0 0 6px #10B981' }} />
                                    <Typography variant="subtitle2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.92rem' }}>
                                        Live Fleet Radar
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.3, borderRadius: '6px', backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                                        <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#059669', fontSize: '0.72rem' }}>
                                            {activeCount} Drivers On Shift
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.3, borderRadius: '6px', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                        <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: '#64748B', fontSize: '0.72rem' }}>
                                            {locations.length} Tracking on Radar
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ height: 430, width: '100%', position: 'relative' }}>
                                {mylocation && mylocation.lat ? (
                                    <GoogleMap
                                        zoom={11}
                                        center={mylocation}
                                        mapContainerStyle={{ height: '100%', width: '100%', fontFamily: FONT_FAMILY }}
                                        options={{
                                            disableDefaultUI: false,
                                            zoomControl: true,
                                            styles: [
                                                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                                                { featureType: 'transit', stylers: [{ visibility: 'off' }] }
                                            ]
                                        }}
                                    >
                                        {locations.map(marker => (
                                            <Marker
                                                position={{ lat: marker.lat, lng: marker.lng }}
                                                key={marker.id}
                                                icon={{
                                                    url: marker.carImage || '/bike.png',
                                                    scaledSize: new window.google.maps.Size(30, 30)
                                                }}
                                            >
                                                <InfoWindow
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
                                                >
                                                    <Box sx={{ fontFamily: FONT_FAMILY, p: 0.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                                            {marker.drivername}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            {marker.cartype} • {marker.carnumber}
                                                        </Typography>
                                                        {marker.bookingRef && (
                                                            <Chip size="small" label={`Order: #${marker.bookingRef}`} sx={{ mt: 0.5, height: 18, fontSize: '0.62rem', fontWeight: 700 }} color="primary" />
                                                        )}
                                                    </Box>
                                                </InfoWindow>
                                            </Marker>
                                        ))}
                                    </GoogleMap>
                                ) : (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC' }}>
                                        <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8' }}>
                                            Connecting to GPS satellites...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    </Grid>

                    {/* Operations Health Panel */}
                    <Grid item xs={12} lg={4}>
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '14px',
                                border: '1px solid #E2E8F0',
                                p: 2.5,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.92rem', mb: 2 }}>
                                    Platform Health & Utilization
                                </Typography>

                                <Stack spacing={2.2}>
                                    {/* Fleet Active Rate */}
                                    <Box sx={{ p: 2, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: '#475569', fontSize: '0.78rem' }}>
                                                Fleet Shift Utilization
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.85rem' }}>
                                                {driverCount > 0 ? `${Math.round((activeCount / driverCount) * 100)}%` : '0%'}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={driverCount > 0 ? Math.round((activeCount / driverCount) * 100) : 0}
                                            sx={{ height: 6, borderRadius: 3, backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#10B981' } }}
                                        />
                                        <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8', fontSize: '0.7rem', mt: 0.8, display: 'block' }}>
                                            {activeCount} of {driverCount} approved drivers currently online
                                        </Typography>
                                    </Box>

                                    {/* Order Fulfillment Rate */}
                                    <Box sx={{ p: 2, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: '#475569', fontSize: '0.78rem' }}>
                                                Order Fulfillment Rate
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.85rem' }}>
                                                {completionRate}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={completionRate}
                                            sx={{ height: 6, borderRadius: 3, backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#2563EB' } }}
                                        />
                                        <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8', fontSize: '0.7rem', mt: 0.8, display: 'block' }}>
                                            {totalCompletedAnnual} completed vs {totalCancelledAnnual} cancellations
                                        </Typography>
                                    </Box>

                                    {/* User Accounts Overview */}
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.72rem', fontWeight: 600 }}>
                                                Customers
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '1.2rem', mt: 0.2 }}>
                                                {customerCount}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.72rem', fontWeight: 600 }}>
                                                Total Drivers
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '1.2rem', mt: 0.2 }}>
                                                {driverCount}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9' }}>
                                <Button
                                    component={Link}
                                    to="/bookings"
                                    fullWidth
                                    variant="text"
                                    endIcon={<ArrowForwardIcon fontSize="small" />}
                                    sx={{
                                        fontFamily: FONT_FAMILY,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#0F172A',
                                        fontSize: '0.82rem',
                                        justifyContent: 'space-between',
                                        py: 1,
                                        px: 1.5,
                                        borderRadius: '8px',
                                        '&:hover': { backgroundColor: '#F1F5F9' }
                                    }}
                                >
                                    Open Dispatch Console
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Bottom Row: Analytics Chart Card ── */}
                <Card
                    elevation={0}
                    sx={{
                        mt: 2.5,
                        backgroundColor: '#FFFFFF',
                        borderRadius: '14px',
                        border: '1px solid #E2E8F0',
                        p: { xs: 2, sm: 2.5 }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.92rem' }}>
                                {t('booking_chart', { defaultValue: 'Annual Delivery & Booking Trends' })}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.75rem' }}>
                                Monthly distribution of fulfilled orders vs customer cancellations
                            </Typography>
                        </Box>
                    </Box>

                    <Chart
                        options={chartOptions}
                        series={chartSeries}
                        type="area"
                        width="100%"
                        height={310}
                    />
                </Card>
            </Box>
    );
};

export default Dashboard;